"use client";
/**
 * Wallet-pass screen (Block 5, design 1.5): first-time prompt + "Add to Apple
 * Wallet" / "Add to Google Wallet" buttons.
 *
 * Both pass endpoints return 503 PASS_NOT_CONFIGURED until ops provisions the
 * signing certs. We probe lazily on tap; on 503 we flip into a graceful
 * "coming soon" disabled state instead of erroring.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Button, Card, TopBar } from "@/components/ui";
import { SignedOutGate } from "@/components/loyalty/LoyaltyBits";
import { passApi, isPassNotConfigured } from "@/api/services";
import { useAuth } from "@/store/auth";

type PassState = "idle" | "loading" | "not_configured" | "error";

export function WalletPassScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoggedIn, user } = useAuth();
  const [state, setState] = useState<PassState>("idle");

  const handleApple = async () => {
    if (!user?.id) return;
    setState("loading");
    try {
      const blob = await passApi.downloadApplePass(user.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tappya-loyalty.pkpass";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState("idle");
    } catch (err) {
      setState(isPassNotConfigured(err) ? "not_configured" : "error");
    }
  };

  const handleGoogle = async () => {
    if (!user?.id) return;
    setState("loading");
    try {
      const { saveUrl } = await passApi.getGooglePass(user.id);
      if (saveUrl) {
        window.open(saveUrl, "_blank", "noopener,noreferrer");
        setState("idle");
      } else {
        setState("error");
      }
    } catch (err) {
      setState(isPassNotConfigured(err) ? "not_configured" : "error");
    }
  };

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.wallet_pass.title")} onBack={() => navigate("/loyalty-wallet")} />
        <SignedOutGate
          icon={<Icons.wallet size={32} />}
          title={t("v2.wallet_pass.signin_title")}
          desc={t("v2.wallet_pass.signin_desc")}
          cta={t("v2.profile.signin_phone")}
        />
      </div>
    );
  }

  const notConfigured = state === "not_configured";
  const busy = state === "loading";

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar title={t("v2.wallet_pass.title")} onBack={() => navigate("/loyalty-wallet")} />

      <div style={{ padding: "16px 16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <Card padding={22} style={{ textAlign: "center" }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 18,
              margin: "0 auto",
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              color: "var(--ink-1)",
            }}
          >
            <Icons.wallet size={28} />
          </div>
          <div className="display" style={{ fontSize: 19, marginTop: 12 }}>
            {t("v2.wallet_pass.prompt_title")}
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 6, padding: "0 12px", lineHeight: 1.45 }}>
            {t("v2.wallet_pass.prompt_desc")}
          </div>

          <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
            <Button
              variant="dark"
              full
              onClick={handleApple}
              disabled={busy || notConfigured}
              leading={<Icons.apple size={18} />}
            >
              {t("v2.wallet_pass.add_apple")}
            </Button>
            <Button
              variant="outline"
              full
              onClick={handleGoogle}
              disabled={busy || notConfigured}
              leading={<Icons.google size={18} />}
            >
              {t("v2.wallet_pass.add_google")}
            </Button>
          </div>
        </Card>

        {notConfigured && (
          <Card padding={14} style={{ background: "var(--bonus-bg)", border: "1px solid #f4d99c" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Icons.info size={18} style={{ color: "var(--bonus)", flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--bonus)" }}>
                  {t("v2.wallet_pass.coming_soon_title")}
                </div>
                <div style={{ fontSize: 12.5, color: "#7a4815", marginTop: 4, lineHeight: 1.45 }}>
                  {t("v2.wallet_pass.coming_soon_desc")}
                </div>
              </div>
            </div>
          </Card>
        )}

        {state === "error" && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.wallet_pass.error")}
            </div>
          </Card>
        )}

        {/* Benefits-of-pass explainer */}
        <Card padding={0}>
          {[
            { ic: Icons.qr, key: "scan" },
            { ic: Icons.spark, key: "earn" },
            { ic: Icons.bell, key: "updates" },
          ].map((row, i) => {
            const Ic = row.ic;
            return (
              <div
                key={row.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 14,
                  borderTop: i === 0 ? 0 : "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    placeItems: "center",
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: "var(--primary-50)",
                    color: "var(--primary-700)",
                    flexShrink: 0,
                  }}
                >
                  <Ic size={18} />
                </div>
                <div style={{ fontSize: 13.5, color: "var(--ink-1)", fontWeight: 600 }}>
                  {t(`v2.wallet_pass.feature_${row.key}`)}
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
}
